import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { IFilterDefinition, ISelectFilterDefinition, OptionType } from '@evinced-private/ui-common';

import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';

import { LabelsExcludedFromDashboard } from '../../../common/types/ConfigurationTypes';
import { DASHBOARD_DATE_RANGE_OPTIONS } from '../constants/DashboardFiltersDefinitions';
import {
	buildLabelsExcludedFromDashboard,
	processPlatformConfiguration
} from '../helpers/ConfigurationProcessingHelpers';
import {
	createFiltersFromAdditionalLabels,
	createFiltersFromCoreLabels
} from '../helpers/FilterCreationHelpers';
import {
	createOptionsFromAdditionalLabels,
	createOptionsFromCoreLabels
} from '../helpers/FilterOptionsHelpers';
import { isBusinessUnitsRoute } from '../helpers/RouteDetectionHelpers';

const DATE_RANGE_FILTER: ISelectFilterDefinition = {
	id: 'dateRange',
	label: 'Date range',
	filterType: 'select',
	defaultValue: [DASHBOARD_DATE_RANGE_OPTIONS[0]],
	componentProps: {
		placeholder: 'Last 30 days',
		isMultiple: false,
		id: 'dateRange',
		label: 'Date range',
		listboxWidth: '180px'
	}
};

export const useConfigurationFilters = (): {
	filterDefinitions: IFilterDefinition[];
	filterOptions: Record<string, OptionType[]>;
	labelsExcludedFromDashboard: LabelsExcludedFromDashboard;
} => {
	const { pathname } = useLocation();
	const { configuration } = useConfiguration();

	const { filterDefinitions, filterOptions, labelsExcludedFromDashboard } = useMemo(() => {
		if (!configuration?.PLATFORM) {
			return {
				filterDefinitions: [DATE_RANGE_FILTER],
				filterOptions: { dateRange: DASHBOARD_DATE_RANGE_OPTIONS },
				labelsExcludedFromDashboard: {}
			};
		}

		const { additionalLabels, coreLabels, isValid } = processPlatformConfiguration(
			configuration.PLATFORM
		);

		if (!isValid) {
			return {
				filterDefinitions: [DATE_RANGE_FILTER],
				filterOptions: { dateRange: DASHBOARD_DATE_RANGE_OPTIONS },
				labelsExcludedFromDashboard: {}
			};
		}

		const additionalFilters = createFiltersFromAdditionalLabels(additionalLabels);
		const coreFilters = createFiltersFromCoreLabels(coreLabels, pathname, isBusinessUnitsRoute);
		const allFilters = [...coreFilters, ...additionalFilters, DATE_RANGE_FILTER];

		const allOptions = { dateRange: DASHBOARD_DATE_RANGE_OPTIONS };

		if (additionalFilters.length > 0) {
			const additionalOptions = createOptionsFromAdditionalLabels(additionalLabels);
			Object.assign(allOptions, additionalOptions);
		}
		if (coreFilters.length > 0) {
			const coreOptions = createOptionsFromCoreLabels(coreLabels, pathname, isBusinessUnitsRoute);
			Object.assign(allOptions, coreOptions);
		}

		const labelsExcludedFromDashboard = buildLabelsExcludedFromDashboard(coreLabels);

		return {
			filterDefinitions: allFilters,
			filterOptions: allOptions,
			labelsExcludedFromDashboard
		};
	}, [configuration, pathname]);

	return { filterDefinitions, filterOptions, labelsExcludedFromDashboard };
};
